import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Video, Calendar, Clock, ExternalLink, Bell, X } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpcomingMeetingBanner = () => {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    fetchUpcomingMeeting();
  }, []);

  useEffect(() => {
    if (!meeting) return;

    const updateTimeLeft = () => {
      const now = new Date();
      const meetingDate = new Date(meeting.meeting_date);
      const diffMs = meetingDate - now;
      
      if (diffMs <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, isNow: true });
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeLeft({
        hours,
        minutes,
        isNow: false,
        isUrgent: hours < 1,
        isSoon: hours < 24
      });
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [meeting]);

  const fetchUpcomingMeeting = async () => {
    try {
      const res = await axios.get(`${API}/meetings/my-upcoming`);
      if (res.data && res.data.length > 0) {
        setMeeting(res.data[0]); // Get the nearest meeting
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMeetingDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const formatMeetingTime = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || !meeting || dismissed) return null;

  const isUrgent = timeLeft?.isUrgent || timeLeft?.isNow;
  const isSoon = timeLeft?.isSoon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        className={`relative rounded-2xl p-6 mb-6 overflow-hidden ${
          isUrgent 
            ? 'bg-gradient-to-r from-red-600 via-red-500 to-orange-500' 
            : isSoon
              ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500'
              : 'bg-gradient-to-r from-indigo-600 via-purple-500 to-blue-500'
        }`}
        data-testid="upcoming-meeting-banner"
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff20_1px,transparent_1px),linear-gradient(to_bottom,#ffffff20_1px,transparent_1px)] bg-[size:20px_20px]" />
        </div>

        {/* Pulsing indicator for urgent meetings */}
        {isUrgent && (
          <div className="absolute top-4 right-16">
            <span className="flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
            </span>
          </div>
        )}

        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              isUrgent ? 'bg-white/30 animate-pulse' : 'bg-white/20'
            }`}>
              <Video className="w-7 h-7 text-white" />
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-1">
                {isUrgent && (
                  <span className="px-2 py-0.5 bg-white/30 rounded-full text-xs font-bold text-white uppercase tracking-wide animate-pulse">
                    {timeLeft?.isNow ? '¡AHORA!' : '¡PRÓXIMAMENTE!'}
                  </span>
                )}
                <h3 className="text-xl font-bold text-white">
                  {isUrgent ? '📹 ¡Tu reunión está por comenzar!' : '📹 Tienes una reunión programada'}
                </h3>
              </div>
              
              <p className="text-white/90 mb-2">
                {meeting.description || 'Reunión con tu oficial migratorio'}
              </p>
              
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatMeetingDate(meeting.meeting_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatMeetingTime(meeting.meeting_date)}
                </span>
                {timeLeft && !timeLeft.isNow && (
                  <span className="flex items-center gap-1 font-medium">
                    <Bell className="w-4 h-4" />
                    {timeLeft.hours > 0 
                      ? `En ${timeLeft.hours}h ${timeLeft.minutes}min`
                      : `En ${timeLeft.minutes} minutos`
                    }
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a 
              href={meeting.zoom_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button 
                className={`${
                  isUrgent 
                    ? 'bg-white text-red-600 hover:bg-gray-100 shadow-lg shadow-red-900/30' 
                    : 'bg-white text-blue-600 hover:bg-gray-100 shadow-lg shadow-blue-900/30'
                } font-bold px-6 py-6 text-base`}
                data-testid="join-meeting-btn"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                {isUrgent ? 'UNIRSE AHORA' : 'Unirse a la Reunión'}
              </Button>
            </a>
          </div>
        </div>

        {/* Countdown bar for urgent meetings */}
        {isSoon && timeLeft && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center justify-between text-white/70 text-sm mb-2">
              <span>Tiempo restante</span>
              <span className="font-medium text-white">
                {timeLeft.hours > 0 
                  ? `${timeLeft.hours} horas ${timeLeft.minutes} minutos`
                  : `${timeLeft.minutes} minutos`
                }
              </span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: '100%' }}
                animate={{ 
                  width: `${Math.max(0, Math.min(100, (timeLeft.hours * 60 + timeLeft.minutes) / (24 * 60) * 100))}%` 
                }}
                className={`h-full rounded-full ${isUrgent ? 'bg-white' : 'bg-white/60'}`}
              />
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default UpcomingMeetingBanner;
